import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";

type Props = {
    children: React.ReactNode;
    enabled?: boolean;
    hideBottomScrollbar?: boolean;
    className?: string;
    scrollableSelector?: string;
};

const TopScrollbarWrapper = ({
    children,
    enabled = true,
    hideBottomScrollbar = true,
    className,
    scrollableSelector,
}: Props) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const topRef = useRef<HTMLDivElement | null>(null);
    const spacerRef = useRef<HTMLDivElement | null>(null);
    const scrollableRef = useRef<HTMLElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null); // our direct container for children
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const [showTop, setShowTop] = useState<boolean>(enabled);

    useEffect(() => {
        setShowTop(enabled);
    }, [enabled]);

    useEffect(() => {
        const wrapper = wrapperRef.current;
        const container = containerRef.current;
        if (!wrapper || !container) return;

        // prefer an explicit selector, otherwise use our containerRef
        const scrollElCandidate = scrollableSelector ? (wrapper.querySelector(scrollableSelector) as HTMLElement | null) : null;
        // find the deepest element that scrolls horizontally inside container
        const isVisible = (el: HTMLElement) => {
            const cs = window.getComputedStyle(el);
            if (cs.display === "none" || cs.visibility === "hidden" || cs.opacity === "0") return false;
            const rect = el.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) return false;
            return true;
        };

        const findDeepScrollable = (root: HTMLElement): HTMLElement | null => {
            const all = root.querySelectorAll<HTMLElement>("*");
            let fallback: HTMLElement | null = null;
            for (let i = all.length - 1; i >= 0; i--) {
                const el = all[i];
                if (!el) continue;
                if (!isVisible(el)) continue;
                if (!(el.scrollWidth > el.clientWidth)) continue;
                const cs = window.getComputedStyle(el);
                const overflowX = (cs.overflowX || "").toLowerCase();
                const containsTable = !!el.querySelector("table");
                // prefer visible elements with explicit horizontal overflow or that contain a table
                if (overflowX === "auto" || overflowX === "scroll" || containsTable) return el;
                // otherwise keep as fallback
                if (!fallback) fallback = el;
            }
            if (fallback) return fallback;
            return isVisible(root) && root.scrollWidth > root.clientWidth ? root : null;
        };

        const detected = scrollElCandidate ?? findDeepScrollable(container) ?? (container.scrollWidth > container.clientWidth ? container : null);
        const scrollEl = detected ?? container;
        scrollableRef.current = scrollEl;
        const top = topRef.current;
        const spacer = spacerRef.current;
        if (!top || !spacer || !scrollEl) return;

        const updateSpacer = () => {
            try {
                spacer.style.width = `${scrollEl.scrollWidth}px`;
            } catch (e) {
                void e;
            }
        };
        updateSpacer();

        if ("ResizeObserver" in window) {
            resizeObserverRef.current = new ResizeObserver(() => {
                updateSpacer();
            });
            resizeObserverRef.current.observe(scrollEl);
            window.addEventListener("resize", updateSpacer, { passive: true });
        }

        const onTopScroll = () => {
            if (scrollEl) scrollEl.scrollLeft = top.scrollLeft;
        };
        const onTableScroll = () => {
            if (top) top.scrollLeft = scrollEl.scrollLeft;
        };
        top.addEventListener("scroll", onTopScroll, { passive: true });
        scrollEl.addEventListener("scroll", onTableScroll, { passive: true });

        let hideClassName: string | null = null;
        let styleEl: HTMLStyleElement | null = null;
        if (hideBottomScrollbar) {
            try {
                hideClassName = `tsw-hide-scroll-${Math.random().toString(36).slice(2, 9)}`;
                scrollEl.classList.add(hideClassName);
                (scrollEl as HTMLElement).style.setProperty("scrollbar-width", "none");
                (scrollEl as HTMLElement).style.overflowY = (scrollEl as HTMLElement).style.overflowY || "visible";
                styleEl = document.createElement("style");
                styleEl.type = "text/css";
                styleEl.appendChild(document.createTextNode(`.${hideClassName}::-webkit-scrollbar{height:0;}`));
                styleEl.appendChild(document.createTextNode(`.${hideClassName}{-ms-overflow-style:none;}`));
                document.head.appendChild(styleEl);
            } catch (e) {
                void e;
            }
        }

        return () => {
            top.removeEventListener("scroll", onTopScroll);
            scrollEl.removeEventListener("scroll", onTableScroll);
            if (resizeObserverRef.current && scrollEl) resizeObserverRef.current.unobserve(scrollEl);
            if (resizeObserverRef.current) resizeObserverRef.current.disconnect();
            window.removeEventListener("resize", updateSpacer);
            if (hideClassName) {
                try {
                    scrollEl.classList.remove(hideClassName);
                } catch (e) {
                    void e;
                }
            }
            if (styleEl && styleEl.parentNode) styleEl.parentNode.removeChild(styleEl);
        };
    }, [className, scrollableSelector, hideBottomScrollbar]);

    const wrapperSx: React.CSSProperties = {
        width: "100%",
    };

    return (
        <Box ref={wrapperRef} className={className} sx={wrapperSx}>
            {showTop && (
                <Box
                    ref={topRef}
                    sx={{
                        overflowX: "auto",
                        overflowY: "hidden",
                        height: "40px",
                        background: "transparent",
                        width: "100%",
                        "&::-webkit-scrollbar": { height: "40px" },
                        "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "rgba(0,0,0,0.35)",
                            borderRadius: "6px",
                        },
                        "&::-webkit-scrollbar-track": { background: "transparent" },
                        scrollbarWidth: "thin",
                        scrollbarColor: "rgba(0,0,0,0.35) transparent",
                        outline: "none",
                    }}
                    tabIndex={0}
                    aria-label="Horizontal scroll"
                >
                        
                    <Box ref={spacerRef} sx={{ width: "1px", minWidth: "100%", height: 1 }} />
                </Box>
            )}

            <Box
                sx={{
                    width: "100%",
                    overflowX: "auto",
                    "&::-webkit-scrollbar": { height: hideBottomScrollbar ? 0 : "8px" },
                    scrollbarWidth: hideBottomScrollbar ? "none" : undefined,
                }}
                ref={containerRef}
            >
                {children}
            </Box>
        </Box>
    );
};

export default TopScrollbarWrapper;
